import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ConfirmDialog,
  Input,
  PageHeader,
  RowMenu,
  StatusBadge,
} from '../../components';
import { ApiError, apiRequest } from '../../lib/api';
import type { ListResponse } from '../../lib/useCrud';
import { ClientFormModal } from './ClientFormModal';
import type { ClientPayload, ClientRow } from './types';
import type { UserOption } from '../leads/types';

const PAGE_SIZE = 25;

export function ClientsListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<ClientRow | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ClientRow | null>(null);

  const listQueryStr = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limit', String(PAGE_SIZE));
    p.set('offset', String(offset));
    p.set('include_inactive', 'true');
    if (q.trim()) p.set('q', q.trim());
    return p.toString();
  }, [q, offset]);

  const clients = useQuery({
    queryKey: ['clients', listQueryStr],
    queryFn: () => apiRequest<ListResponse<ClientRow>>(`/clients?${listQueryStr}`),
  });
  const usersQ = useQuery({
    queryKey: ['lookup', 'users'],
    queryFn: () =>
      apiRequest<ListResponse<UserOption>>('/users?limit=200&include_inactive=false'),
  });
  const users = usersQ.data?.data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['clients'] });

  const createMut = useMutation({
    mutationFn: (body: ClientPayload) =>
      apiRequest<{ data: ClientRow }>('/clients', { method: 'POST', body }),
    onSuccess: invalidate,
  });
  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: ClientPayload }) =>
      apiRequest<{ data: ClientRow }>(`/clients/${args.id}`, {
        method: 'PATCH',
        body: args.body,
      }),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/clients/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const total = clients.data?.pagination?.total ?? 0;
  const end = Math.min(offset + PAGE_SIZE, total);

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle="Accounts that have been converted from leads or added directly."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            New client
          </Button>
        }
      />

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body">
          <Input
            icon="search"
            placeholder="Search name, email, phone, company"
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
          />
        </div>
      </div>

      {clients.isLoading && <div className="card card-body muted">Loading…</div>}
      {clients.error && (
        <div className="alert">
          {clients.error instanceof Error ? clients.error.message : 'Failed to load'}
        </div>
      )}
      {clients.data && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {clients.data.data.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    No clients yet.
                  </td>
                </tr>
              )}
              {clients.data.data.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/clients/${c.id}`}>{c.full_name ?? 'Unnamed'}</Link>
                  </td>
                  <td>{c.company ?? <span className="muted">—</span>}</td>
                  <td className="cell-mono">{c.email ?? <span className="muted">—</span>}</td>
                  <td>{c.phone ?? <span className="muted">—</span>}</td>
                  <td>
                    <StatusBadge active={c.is_active} />
                  </td>
                  <td>
                    <RowMenu
                      items={[
                        {
                          icon: 'arrowRight',
                          label: 'Open',
                          onClick: () => navigate(`/clients/${c.id}`),
                        },
                        { icon: 'edit', label: 'Edit', onClick: () => setEditing(c) },
                        'divider',
                        {
                          icon: 'trash',
                          label: 'Delete',
                          onClick: () => setDeleting(c),
                          danger: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            className="card-header"
            style={{
              justifyContent: 'space-between',
              borderTop: '1px solid var(--border)',
              borderBottom: 'none',
            }}
          >
            <span className="muted" style={{ fontSize: 12 }}>
              {total === 0 ? '0' : `${offset + 1}–${end}`} of {total}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button disabled={end >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <ClientFormModal
          row={editing === 'new' ? undefined : editing}
          users={users}
          onClose={() => setEditing(null)}
          onSubmit={async (payload) => {
            if (editing === 'new') {
              await createMut.mutateAsync(payload);
            } else {
              await updateMut.mutateAsync({ id: editing.id, body: payload });
            }
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete client"
        message={
          deleting ? (
            <>
              Delete <strong>{deleting.full_name ?? 'this client'}</strong>? This cannot be undone.
            </>
          ) : null
        }
        destructive
        confirmLabel="Delete"
        loading={deleteMut.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await deleteMut.mutateAsync(deleting.id);
            setDeleting(null);
          } catch (err) {
            alert(err instanceof ApiError ? err.message : 'Delete failed');
          }
        }}
      />
    </>
  );
}
